import { formatBRL as formatBRLCurrency } from 'dashboard/helper/lais/currency';

export const COMMISSION_GROUP_BYS = ['agent', 'team', 'agency'];
export const COMMISSION_SOURCE_TYPES = ['offer', 'ccv', 'visit', 'manual'];
export const COMMISSION_STATUSES = ['pending', 'confirmed', 'paid', 'canceled'];

export const COMMISSION_TRACKING_EVENTS = Object.freeze({
  viewed: 'lais_commission_viewed',
  closed: 'lais_commission_period_closed',
  reopened: 'lais_commission_period_reopened',
  targetSet: 'lais_commission_target_set',
  exported: 'lais_commission_exported',
  manualCreated: 'lais_commission_manual_created',
  settlementMarked: 'lais_commission_settlement_marked',
});

export const currentMonthKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  return `${year}-${month}`;
};

export const formatBRL = value => {
  const number = Number(value || 0);
  return formatBRLCurrency(Number.isFinite(number) ? number : 0);
};

export const formatCentsBRL = cents => formatBRL(Number(cents || 0) / 100);

export const formatAttainment = value => {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toFixed(1)}%`;
};

// Largura da barra de progresso (0–100), nunca acima de 100 visualmente.
export const attainmentWidth = value => {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number <= 0) return 0;
  return Math.min(Math.round(number), 100);
};

export const isBehindTarget = row =>
  Boolean(row?.meta) &&
  Number(row.meta) > 0 &&
  Number(row.atingimento || 0) < 100;

export const attainmentBarClass = row => {
  if (!row?.meta || Number(row.meta) <= 0) return 'bg-n-slate-6';
  return Number(row.atingimento || 0) >= 100 ? 'bg-n-teal-9' : 'bg-n-ruby-9';
};

export const commissionRowKey = row =>
  String(row?.agent_id || row?.team_id || row?.id || 'unassigned');

export const sourceBreakdownEntries = row => {
  const bySource = row?.por_origem || {};
  const total = Number(row?.valor || 0);

  return COMMISSION_SOURCE_TYPES.map(sourceType => {
    const value = Number(bySource[sourceType] || 0);
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return {
      sourceType,
      value,
      percentage: Number.isFinite(percentage) ? percentage : 0,
    };
  });
};

export const activeSourceCount = row =>
  sourceBreakdownEntries(row).filter(entry => entry.value > 0).length;

export const normalizeReport = (payload = {}) => ({
  periodo: payload.periodo || {},
  filtros: payload.filtros || {},
  totais: payload.totais || {
    valor: 0,
    meta: 0,
    atingimento: null,
    qtd: 0,
    nao_atribuida: 0,
  },
  serieMensal: Array.isArray(payload.serie_mensal) ? payload.serie_mensal : [],
  breakdown: Array.isArray(payload.breakdown) ? payload.breakdown : [],
  rollup: Array.isArray(payload.rollup) ? payload.rollup : [],
});

export const normalizeReconciliationSplit = split => ({
  id: split?.id,
  sequence: split?.sequence,
  role: split?.role || '',
  beneficiary_kind: split?.beneficiary_kind || '',
  beneficiary_user_id: split?.beneficiary_user_id || null,
  beneficiary_team_id: split?.beneficiary_team_id || null,
  beneficiary_external_ref: split?.beneficiary_external_ref || '',
  beneficiary_label: split?.beneficiary_label || '',
  percentage: split?.percentage || '0',
  base_amount_cents: Number(split?.base_amount_cents || 0),
  base_amount: split?.base_amount || '0.00',
  amount_cents: Number(split?.amount_cents || 0),
  amount: split?.amount || '0.00',
  currency: split?.currency || 'BRL',
  status: split?.status || 'unknown',
  metadata: split?.metadata || {},
});

export const normalizeLedgerEntry = entry => ({
  id: entry?.id,
  commission_split_id: entry?.commission_split_id || null,
  scope: entry?.scope || 'pool',
  event_type: entry?.event_type || '',
  entry_key: entry?.entry_key || '',
  amount_cents: Number(entry?.amount_cents || 0),
  amount: entry?.amount || '0.00',
  currency: entry?.currency || 'BRL',
  effective_period: entry?.effective_period || '',
  recorded_at: entry?.recorded_at || '',
  actor_type: entry?.actor_type || '',
  actor_id: entry?.actor_id || null,
  compensates_entry_id: entry?.compensates_entry_id || null,
  metadata: entry?.metadata || {},
});

export const normalizeReconciliationPool = pool => ({
  id: pool?.id,
  period: pool?.period || '',
  source_type: pool?.source_type || '',
  source_id: pool?.source_id,
  status: pool?.status || 'unknown',
  currency: pool?.currency || 'BRL',
  net_amount_cents: Number(pool?.net_amount_cents || 0),
  net_amount: pool?.net_amount || '0.00',
  split_amount_cents: Number(pool?.split_amount_cents || 0),
  ledger_pool_balance_cents: Number(pool?.ledger_pool_balance_cents || 0),
  ledger_split_balance_cents: Number(pool?.ledger_split_balance_cents || 0),
  payable_pool_cents: Number(pool?.payable_pool_cents || 0),
  payable_split_cents: Number(pool?.payable_split_cents || 0),
  paid_pool_cents: Number(pool?.paid_pool_cents || 0),
  paid_split_cents: Number(pool?.paid_split_cents || 0),
  balanced: Boolean(pool?.balanced),
  issues: Array.isArray(pool?.issues) ? pool.issues : [],
  checks: pool?.checks || {},
  counts: pool?.counts || {},
  transaction: pool?.transaction || {},
  agreement: pool?.agreement || null,
  splits: Array.isArray(pool?.splits)
    ? pool.splits.map(normalizeReconciliationSplit)
    : [],
  ledger_entries: Array.isArray(pool?.ledger_entries)
    ? pool.ledger_entries.map(normalizeLedgerEntry)
    : [],
});

export const normalizeReconciliation = (payload = {}) => ({
  periodo: payload.periodo || {},
  resumo: payload.resumo || {
    total_pools: 0,
    balanced_pools: 0,
    unbalanced_pools: 0,
    net_amount_cents: 0,
    net_amount: '0.00',
    issue_counts: {},
  },
  pools: Array.isArray(payload.pools)
    ? payload.pools.map(normalizeReconciliationPool)
    : [],
  issueTypes: Array.isArray(payload.issue_types) ? payload.issue_types : [],
});

export const settlementStatusTone = status => {
  if (status === 'paid') return 'positive';
  if (status === 'payable') return 'info';
  if (status === 'reversed' || status === 'canceled') return 'danger';
  if (status === 'accrued' || status === 'confirmed') return 'warning';
  return 'neutral';
};

export const canMarkPoolPayable = pool =>
  Boolean(pool?.balanced) && ['accrued', 'confirmed'].includes(pool?.status);

export const canMarkPoolPaid = pool =>
  Boolean(pool?.balanced) && pool?.status === 'payable';

export const settlementPendingKey = pool => {
  if (['accrued', 'confirmed'].includes(pool?.status)) return 'PAYABLE_PENDING';
  if (pool?.status === 'payable') return 'PAYMENT_PENDING';
  return '';
};

export const commissionTrackingPayload = report => ({
  group_by: report?.filtros?.group_by || 'agent',
  total: report?.totais?.valor || 0,
  count: report?.totais?.qtd || 0,
  own_only: Boolean(report?.filtros?.somente_proprio),
});
